import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from './auth.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	private isLoggingOut = false;

	constructor(
		private messageService: MessageService,
		private zone: NgZone,
		private router: Router,
		private authService: AuthService,
	) {}

	handleError(error: unknown) {
		console.log(error);
		this.zone.run(() => {
			// Gérer les erreurs HTTP
			if (error instanceof HttpErrorResponse) {
				this.handleHttpError(error);
				return;
			}

			// Gérer les erreurs avec une propriété status (erreurs HTTP non typées)
			if (typeof error === 'object' && error !== null && 'status' in error) {
				const errorWithStatus = error as { status: number; statusText?: string };
				if (errorWithStatus.status === 401 || errorWithStatus.status === 403) {
					this.handleUnauthorizedError(errorWithStatus.status);
					return;
				}
				this.messageService.add({
					severity: 'error',
					summary: errorWithStatus.statusText || 'Erreur',
					detail: `Status ${errorWithStatus.status}`,
				});
			}
		});
	}

	private handleHttpError(error: HttpErrorResponse) {
		if (error.status === 401 || error.status === 403) {
			this.handleUnauthorizedError(error.status);
		} else {
			this.messageService.add({
				severity: 'error',
				summary: error.statusText || 'Erreur',
				detail: `Status ${error.status}`,
			});
		}
	}

	private handleUnauthorizedError(status: number) {
		// Éviter les déconnexions multiples (si déjà géré par l'intercepteur)
		if (this.isLoggingOut) {
			return;
		}
		this.isLoggingOut = true;

		const message =
			status === 401
				? 'Votre session a expiré. Vous allez être déconnecté dans quelques secondes.'
				: "Vous n'avez pas les autorisations nécessaires pour accéder à cette ressource. Vous allez être déconnecté dans quelques secondes.";

		// Afficher le message immédiatement (déjà dans zone.run() depuis handleError)
		this.messageService.add({
			severity: 'error',
			summary: status === 401 ? 'Session expirée' : 'Accès refusé',
			detail: message,
			life: 5000,
		});

		// Déconnecter après 5 secondes
		setTimeout(() => {
			this.zone.run(() => {
				this.authService.logout();
				this.router.navigate(['/login'], {});
			});
		}, 5000);
	}
}
